import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import { permissionsRules } from "constants/constants"
import { IOptionGroup, UserRole } from "interfaces/dataInterfaces"
import { OptionGroup } from "interfaces/mappingInterfaces"
import { IChoices, ListOptionGroup } from "interfaces/xlsForm"
import moment from "moment"
import { FilterOptionOption } from "react-select/dist/declarations/src/filters"
import { getModulePath } from "routes/routePermissions"
import { SortDirection, SortDirectionType } from "react-virtualized"

export function formatDate(date = "", format?: string) {
  if (!date) return ""
  if (format) return moment(new Date(date)).format(format)
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }
  return new Intl.DateTimeFormat("en-GB", options).format(new Date(date))
}

export const parseJwt = (token: string) => {
  try {
    return JSON.parse(atob(token.split(".")[1]))
  } catch (e) {
    return null
  }
}

export const checkAccess = (roles: UserRole[], moduleIds: number[], subModule: string, feature: string) => {
  let featureAccess = false
  const roleList = roles || []
  moduleIds.forEach(moduleId => {
    if (featureAccess) return
    const moduleRole = roleList.find(role => role.applicationModuleId === moduleId)?.roleName?.trim()
    if (!moduleRole) return
    featureAccess = !!permissionsRules?.[moduleId]?.[moduleRole]?.[subModule]?.[feature]
  })
  return featureAccess
}

export const hasModuleRole = (roles: UserRole[], moduleId: number): boolean =>
  !!(roles || []).find(role => role.applicationModuleId === moduleId)?.roleName

export const getAvailableModulePath = (roles: UserRole[], moduleOrder: number[]): string | null => {
  const availableModuleId = moduleOrder.find(moduleId => hasModuleRole(roles, moduleId))
  if (!availableModuleId) return null
  return getModulePath(availableModuleId)
}

export const blobToJsonView = async (response: Response, message = "File not found!", skipType = "text/plain") => {
  const file = await response.blob()
  if (file.type && file.type !== skipType) {
    const beautyJSON = await file.text()
    window.open(
      window.URL.createObjectURL(new Blob(["\ufeff", JSON.stringify(JSON.parse(beautyJSON), null, 2)])),
      "_blank",
    )
  } else alert(message)
}

export const blobToDownload = async (
  response: Response,
  name = "download",
  message = "File not found!",
  skipType = "text/plain",
) => {
  const file = await response.blob()
  if (file.type && file.type !== skipType) {
    // window.location.assign(window.URL.createObjectURL(file)) // old download method doesn't download CSV instead opens them
    const link = document.createElement("a")
    link.href = window.URL.createObjectURL(file)
    if (name) link.download = name
    link.style.display = "none"
    document.body.appendChild(link)
    link.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }))
    link.remove()
    window.URL.revokeObjectURL(link.href)
  } else alert(message)
}

export function sortList<T>({
  fieldList,
  sortBy,
  sortDirection,
}: {
  fieldList?: T[]
  sortBy: string
  sortDirection: SortDirectionType
}): T[] {
  const clonedList = fieldList ? [...fieldList] : []
  if (clonedList.length) {
    clonedList.sort((a, b) => (a[sortBy] >= b[sortBy] ? 1 : -1))
    sortDirection === SortDirection.DESC && clonedList.reverse()
  }
  return clonedList
}

export function searchByString<T>(list: T[], searchValue: string, searchBy: string[]): T[] {
  if (!searchValue || !searchValue.length) return list
  const [firstField, secondField] = searchBy
  const searchValueLowerCase = searchValue.trim().toLowerCase()

  return list.filter(item => {
    const firstValue = item[firstField]?.trim().toLowerCase() as string
    const secondValue = secondField ? (item[secondField]?.trim().toLowerCase() as string) : ""
    return firstValue?.includes(searchValueLowerCase) || secondValue?.includes(searchValueLowerCase)
  })
}

type GroupObj = {
  [key: string]: GroupObj | null
}

export const getGroupList = <T>(list: T[], field: string): OptionGroup[] => {
  if (!list?.length) return []

  const groupObj: GroupObj = {}

  const updateGroupObj = (path: string[], group: GroupObj | null) => {
    if (path.length === 0 || !group) return
    const key = path[0] || "Other"

    if (!group[key]) {
      group[key] = path.length > 1 ? {} : null
    }

    updateGroupObj(path.slice(1), group[key])
  }

  const buildList = (items: T[]) => {
    if (items.length === 0) return

    const path: string[] = items[0][field]?.split("/")

    updateGroupObj(path, groupObj)
    buildList(items.slice(1))
  }

  const convertToOptionGroup = (group: GroupObj | null): OptionGroup[] => {
    if (!group) return []
    return Object.keys(group)?.map(key => {
      return {
        label: key,
        value: key,
        children: group[key] ? convertToOptionGroup(group[key]) : [],
      }
    })
  }

  buildList(list)

  return convertToOptionGroup(groupObj)
}

export const transformResponse = async (response: Response, format: string): Promise<FetchBaseQueryError | void> => {
  if (!response.ok) {
    const res: FetchBaseQueryError = await response.json()
    return res
  }
  format === "csv" ? blobToDownload(response) : blobToJsonView(response)
}

export const filterOption = (option: FilterOptionOption<OptionGroup>, inputValue: string): boolean => {
  const searchValue = inputValue.toLowerCase()

  const checkChildren = (children: OptionGroup[]): boolean => {
    if (!children?.length) return false
    return children?.some(
      child => child.label.toLowerCase().includes(searchValue) || checkChildren(child.children || []),
    )
  }

  return option.label.toLowerCase().includes(searchValue) || checkChildren(option.data?.children || [])
}

export const transformOptionGroup = (items: IOptionGroup[]): OptionGroup[] => {
  return items.map(item => {
    const newItem: OptionGroup = {
      label: item.name,
      value: item.value?.toString(),
      children: item.children ? transformOptionGroup(item.children) : [],
    }
    return newItem
  })
}

export const transformList = (list: IChoices[]): ListOptionGroup[] => {
  const emptyLists: IChoices[] = []
  const noEmptyLists: IChoices[] = []

  list.forEach(item => {
    item?.count === 0 || item.choices?.length === 0 ? emptyLists.push(item) : noEmptyLists.push(item)
  })

  return [
    {
      label: "Empty lists",
      options: emptyLists.sort((a, b) => a.listName.toLowerCase().localeCompare(b.listName.toLowerCase())),
    },
    {
      label: "Lists",
      options: noEmptyLists.sort((a, b) => a.listName.toLowerCase().localeCompare(b.listName.toLowerCase())),
    },
  ]
}

export const normalizeDate = (date: string): string => date?.split("T")?.[0] ?? ""

export const normalizeText = (value?: string): string => {
  if (!value) return ""
  return value
    .trim()
    .replaceAll(/[^\w\s-]/g, "")
    .replaceAll(/\s+/g, "_")
    .replaceAll(/_+/g, "_")
    .replaceAll(/^_+|_+$/g, "")
}
